import { Eye, EyeOff } from 'lucide-react';

import { Button } from '@/components/TailwindComponents/base/Button/Button';
import {
  narrativePlainText,
  safeText,
} from '@/pages/CFS/reports/cfsReport/mappers';
import type {
  CFSReportContext,
  CFSReportFieldDef,
  CFSReportSectionDef,
} from '@/pages/CFS/reports/cfsReport/types';

import type { CFSItemRedactions } from '../data';

interface CFSSectionRendererProps {
  section: CFSReportSectionDef;
  ctx: CFSReportContext;
  sectionRedactions: Record<number, CFSItemRedactions>;
  onToggleField: (itemIndex: number, fieldKey: string) => void;
  activeSearchTerm: string;
  currentMatchKey: string | null;
}

const escapeRegex = (word: string) =>
  word.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const fieldValueAsString = (
  field: CFSReportFieldDef,
  ctx: CFSReportContext,
  item: any,
): string => {
  let resolvedValue: any;
  try {
    resolvedValue = field.value(ctx, item);
  } catch {
    return '';
  }
  if (resolvedValue && typeof resolvedValue === 'object') {
    if ('_narrativeContent' in resolvedValue) {
      return safeText(narrativePlainText(resolvedValue._narrativeContent));
    }
    if ('text' in resolvedValue && typeof resolvedValue.text === 'string') {
      return resolvedValue.text;
    }
    return '';
  }
  return safeText(resolvedValue);
};

interface Mark {
  start: number;
  end: number;
  matchText: string;
  isRedacted: boolean;
  isCurrent: boolean;
  matchKey: string;
}

const parseOccurrenceId = (occurrenceId: string) => {
  const parts = occurrenceId.split(':');
  if (parts.length < 4) return null;
  const wordLower = parts[parts.length - 1];
  const endIndex = Number(parts[parts.length - 2]);
  const startIndex = Number(parts[parts.length - 3]);
  const fieldKey = parts.slice(0, parts.length - 3).join(':');
  if (Number.isNaN(startIndex) || Number.isNaN(endIndex)) return null;
  return { fieldKey, startIndex, endIndex, wordLower };
};

const renderTextWithHighlights = (
  text: string,
  searchTerm: string,
  redactedWords: Set<string>,
  redactedOccurrences: Set<string>,
  fieldKey: string,
  sectionKey: string,
  itemIndex: number,
  currentMatchKey: string | null,
  isFieldRedacted: boolean,
) => {
  if (!text) return null;

  const trimmedSearch = searchTerm.trim();
  const marks: Mark[] = [];

  // Pass 1: live search term highlights.
  if (trimmedSearch) {
    const searchPattern = new RegExp(
      `(?<!\\p{L})${escapeRegex(trimmedSearch)}(?!\\p{L})`,
      'giu',
    );
    let match: RegExpExecArray | null;
    // eslint-disable-next-line no-cond-assign
    while ((match = searchPattern.exec(text)) !== null) {
      const matched = match[0];
      const startIndex = match.index;
      const endIndex = startIndex + matched.length;
      const occurrenceId = `${fieldKey}:${startIndex}:${endIndex}:${matched.toLowerCase()}`;
      const matchKey = `${sectionKey}:${itemIndex}:${occurrenceId}`;
      const isWordRedacted = Array.from(redactedWords).some(
        (redactedWord) => redactedWord.toLowerCase() === matched.toLowerCase(),
      );
      const isOccurrenceRedacted = redactedOccurrences.has(occurrenceId);
      marks.push({
        start: startIndex,
        end: endIndex,
        matchText: matched,
        isRedacted: isWordRedacted || isOccurrenceRedacted,
        isCurrent: currentMatchKey === matchKey,
        matchKey,
      });
    }
  }

  // Pass 2: per-occurrence redactions stored in state (covers case where
  // search term was cleared but redactions persist).
  redactedOccurrences.forEach((occurrenceId) => {
    const parsed = parseOccurrenceId(occurrenceId);
    if (!parsed) return;
    if (parsed.fieldKey !== fieldKey) return;
    const { startIndex, endIndex, wordLower } = parsed;
    if (startIndex < 0 || endIndex > text.length) return;
    const actual = text.slice(startIndex, endIndex);
    if (actual.toLowerCase() !== wordLower) return;
    if (marks.some((mk) => mk.start === startIndex && mk.end === endIndex)) {
      return;
    }
    const matchKey = `${sectionKey}:${itemIndex}:${occurrenceId}`;
    marks.push({
      start: startIndex,
      end: endIndex,
      matchText: actual,
      isRedacted: true,
      isCurrent: currentMatchKey === matchKey,
      matchKey,
    });
  });

  // Pass 3: legacy whole-word redactions (back-compat with addWordRedaction).
  if (redactedWords.size) {
    const wordsArray = Array.from(redactedWords).filter(Boolean);
    if (wordsArray.length) {
      const wordPattern = new RegExp(
        `(?<!\\p{L})(${wordsArray.map(escapeRegex).join('|')})(?!\\p{L})`,
        'giu',
      );
      let match: RegExpExecArray | null;
      // eslint-disable-next-line no-cond-assign
      while ((match = wordPattern.exec(text)) !== null) {
        const matched = match[0];
        const startIndex = match.index;
        const endIndex = startIndex + matched.length;
        if (
          marks.some((mk) => mk.start === startIndex && mk.end === endIndex)
        ) {
          continue;
        }
        const occurrenceId = `${fieldKey}:${startIndex}:${endIndex}:${matched.toLowerCase()}`;
        marks.push({
          start: startIndex,
          end: endIndex,
          matchText: matched,
          isRedacted: true,
          isCurrent: false,
          matchKey: `${sectionKey}:${itemIndex}:${occurrenceId}`,
        });
      }
    }
  }

  if (!marks.length) return <>{text}</>;

  marks.sort((a, b) => a.start - b.start);

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  marks.forEach((mark, idx) => {
    // Skip overlapping marks (defensive, should be rare).
    if (mark.start < lastIndex) return;
    if (mark.start > lastIndex) parts.push(text.slice(lastIndex, mark.start));
    const showStrikethrough =
      (mark.isRedacted || isFieldRedacted) && mark.isCurrent;
    const markStyle: React.CSSProperties = {
      backgroundColor: mark.isCurrent
        ? '#ff9800'
        : mark.isRedacted
        ? '#E7EAEC'
        : '#ffeb3b',
      color: '#000',
      padding: '0 2px',
      borderRadius: '2px',
      fontWeight: mark.isCurrent ? 'bold' : 'normal',
      border: mark.isCurrent ? '2px solid #f57c00' : 'none',
      textDecoration: showStrikethrough ? 'line-through' : 'none',
      textDecorationColor: showStrikethrough ? '#E7EAEC' : 'transparent',
      textDecorationThickness: showStrikethrough ? '2px' : '0',
    };
    parts.push(
      <span
        key={`${mark.isRedacted ? 'r' : 'm'}-${idx}-${mark.start}`}
        data-cfs-match-key={mark.matchKey}
        style={markStyle}
      >
        {mark.matchText}
      </span>,
    );
    lastIndex = mark.end;
  });
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return <>{parts}</>;
};

const renderFieldRow = (
  field: CFSReportFieldDef,
  ctx: CFSReportContext,
  item: any,
  itemIndex: number,
  itemRedactions: CFSItemRedactions,
  onToggleField: CFSSectionRendererProps['onToggleField'],
  activeSearchTerm: string,
  currentMatchKey: string | null,
  sectionKey: string,
) => {
  if (field.hidden && field.hidden(ctx, item)) return null;
  const isRedacted = itemRedactions.redactedKeys.has(field.key);
  const text = fieldValueAsString(field, ctx, item);
  const matchKeyPrefix = `${sectionKey}:${itemIndex}:${field.key}`;
  const span = 1;

  return (
    <div
      key={field.key}
      className="flex flex-row items-start gap-3 py-1 text-xs min-w-0"
      data-cfs-field-key={matchKeyPrefix}
      style={{ gridColumn: `span ${span} / span ${span}` }}
    >
      <div className="flex flex-row items-baseline gap-1 flex-1 min-w-0">
        <span className="font-medium whitespace-nowrap">{field.label}:</span>
        <span
          className={`min-w-0 ${isRedacted ? 'flex-1 px-1' : ''}`}
          style={
            isRedacted ? { backgroundColor: 'rgb(231 234 236)' } : undefined
          }
        >
          {renderTextWithHighlights(
            text,
            activeSearchTerm,
            itemRedactions.redactedWords,
            itemRedactions.redactedOccurrences,
            field.key,
            sectionKey,
            itemIndex,
            currentMatchKey,
            isRedacted,
          )}
          &nbsp;
        </span>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 ml-0.5 p-0.5 print:hidden text-red-500 hover:text-red-600"
        onClick={() => onToggleField(itemIndex, field.key)}
        aria-label={isRedacted ? 'Show' : 'Hide'}
      >
        {isRedacted ? (
          <Eye className="h-4 w-4" />
        ) : (
          <EyeOff className="h-4 w-4" />
        )}
      </Button>
    </div>
  );
};

const CFSSectionRenderer = ({
  section,
  ctx,
  sectionRedactions,
  onToggleField,
  activeSearchTerm,
  currentMatchKey,
}: CFSSectionRendererProps) => {
  const layout = section.detail;
  if (!layout) return null;

  const items = section.items ? section.items(ctx) || [] : [undefined];

  if (section.items && items.length === 0) {
    return (
      <div className="text-xs italic text-gray-500 dark:text-gray-400">
        No {section.title.toLowerCase()}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {items.map((item, itemIndex) => {
        const itemRedactions = sectionRedactions[itemIndex] ?? {
          redactedKeys: new Set<string>(),
          redactedWords: new Set<string>(),
          redactedOccurrences: new Set<string>(),
        };

        return (
          <div
            key={itemIndex}
            className={section.items ? 'dark:border-gray-700 p-2' : ''}
          >
            {section.items && (
              <div className="bg-gray-50 dark:bg-gray-800 px-3 py-2 min-h-[28px] text-gray-900 dark:text-gray-100 text-xs">
                {section.title} {itemIndex + 1}
              </div>
            )}
            <div
              className="grid gap-x-3 gap-y-1 items-start"
              style={{
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gridAutoFlow: 'dense',
              }}
            >
              {layout.fields.map((field) =>
                renderFieldRow(
                  field,
                  ctx,
                  item,
                  itemIndex,
                  itemRedactions,
                  onToggleField,
                  activeSearchTerm,
                  currentMatchKey,
                  section.key,
                ),
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CFSSectionRenderer;
